import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { cn, formatCurrency } from '@/lib/utils';

interface CreditLimitFormData {
  creditLimitEnabled: boolean;
  creditLimit: number;
  creditCurrency: string;
}

interface CreditLimitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  customerName: string;
  currentCreditLimitEnabled: boolean;
  currentCreditLimit: number;
  currentCreditCurrency: string;
}

export function CreditLimitDialog({
  open,
  onOpenChange,
  customerId,
  customerName,
  currentCreditLimitEnabled,
  currentCreditLimit,
  currentCreditCurrency,
}: CreditLimitDialogProps) {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<CreditLimitFormData>({
    defaultValues: {
      creditLimitEnabled: currentCreditLimitEnabled,
      creditLimit: currentCreditLimit,
      creditCurrency: currentCreditCurrency,
    },
  });

  const utils = trpc.useUtils();

  // Fetch current credit status
  const { data: creditStatus, isLoading: isLoadingStatus } =
    trpc.tradingCustomer.getCreditStatus.useQuery(customerId, {
      enabled: open,
    });

  const updateMutation = trpc.tradingCustomer.updateCreditLimit.useMutation({
    onSuccess: () => {
      utils.tradingCustomer.byId.invalidate(customerId);
      utils.tradingCustomer.getCreditStatus.invalidate(customerId);
      showSuccessToast(t('customers.credit.creditLimitUpdated'));
      onOpenChange(false);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        creditLimitEnabled: currentCreditLimitEnabled,
        creditLimit: currentCreditLimit,
        creditCurrency: currentCreditCurrency,
      });
    }
  }, [
    open,
    currentCreditLimitEnabled,
    currentCreditLimit,
    currentCreditCurrency,
    reset,
  ]);

  const onSubmit = (data: CreditLimitFormData) => {
    updateMutation.mutate({
      id: customerId,
      creditLimitEnabled: data.creditLimitEnabled,
      creditLimit: data.creditLimit,
      creditCurrency: data.creditCurrency,
    });
  };

  const creditLimitEnabled = watch('creditLimitEnabled');
  const isSubmitting = updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('customers.credit.manageCreditLimit')}</DialogTitle>
          <DialogDescription>{customerName}</DialogDescription>
        </DialogHeader>

        {isLoadingStatus ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <>
            {creditStatus && creditStatus.creditLimitEnabled && (
              <div
                className={cn(
                  'p-4 rounded-lg border',
                  creditStatus.isOverLimit
                    ? 'bg-red-50 border-red-200'
                    : 'bg-green-50 border-green-200'
                )}
              >
                <div className="flex items-start gap-3">
                  {creditStatus.isOverLimit ? (
                    <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                  ) : (
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  )}
                  <div className="space-y-1">
                    <p
                      className={cn(
                        'font-medium',
                        creditStatus.isOverLimit
                          ? 'text-red-700'
                          : 'text-green-700'
                      )}
                    >
                      {creditStatus.isOverLimit
                        ? t('customers.credit.overLimit')
                        : t('customers.credit.withinLimit')}
                    </p>
                    <div className="text-sm space-y-0.5">
                      <p>
                        {t('customers.credit.creditLimit')}:{' '}
                        <strong>
                          {formatCurrency(
                            creditStatus.creditLimit,
                            creditStatus.creditCurrency
                          )}
                        </strong>
                      </p>
                      <p>
                        {t('customers.credit.outstandingBalance')}:{' '}
                        <strong>
                          {formatCurrency(
                            creditStatus.outstandingBalance,
                            creditStatus.creditCurrency
                          )}
                        </strong>
                      </p>
                      <p>
                        {t('customers.credit.availableCredit')}:{' '}
                        <strong
                          className={
                            creditStatus.isOverLimit ? 'text-red-600' : ''
                          }
                        >
                          {formatCurrency(
                            creditStatus.availableCredit ?? 0,
                            creditStatus.creditCurrency
                          )}
                        </strong>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="creditLimitEnabled"
                  {...register('creditLimitEnabled')}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="creditLimitEnabled">
                  {t('customers.credit.enableCreditLimit')}
                </Label>
              </div>

              {creditLimitEnabled && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="creditLimit">
                      {t('customers.credit.creditLimitAmount')}
                    </Label>
                    <Input
                      id="creditLimit"
                      type="number"
                      step="0.01"
                      min="0"
                      {...register('creditLimit', {
                        valueAsNumber: true,
                        min: {
                          value: 0,
                          message: t('customers.credit.creditLimitMin'),
                        },
                      })}
                    />
                    {errors.creditLimit && (
                      <p className="text-sm text-red-500">
                        {errors.creditLimit.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="creditCurrency">
                      {t('customers.credit.currency')}
                    </Label>
                    <Input
                      id="creditCurrency"
                      {...register('creditCurrency')}
                    />
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  {tCommon('cancel')}
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {tCommon('save')}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
